// Libraries
import React, { MouseEvent, useEffect, useRef, useState } from 'react'

// Images
import {
  default as logoWhite,
  default as logoWhiteTm,
} from '../../../../static-cdn/images/ui-footer-white-logo.svg'
import downArrowWhite from '../../../../static/steam-img/arrows/down_arrow_white.svg'
import upArrowWhite from '../../../../static/steam-img/arrows/up_arrow_white.svg'

// Constants
import { ModuleNames } from '../../../constants/componentNames.constants'
import { LanguageSelect, LocaleCodes } from '../../../constants/localization.constants'

// Helpers
import { returnMatchingLocaleInfo } from '../../../utils/functions.utils'
import {
  createLinkBasedOnType,
  internalLinkHandler,
  isExternalURL,
} from '../../../utils/link.utils'
import { mapLocale } from '../../../utils/pageCreation.utils'

// Styles
import { WeChatPopUp } from '../../../utils/cn.utils'
import WrapperImage from '../wrapperImage'
import { FooterWrapper } from './style'
import { CopyRightLinks, DisclaimerText, SocialMediaIcons } from './subcomponents/socialMedia'

export interface NavigationResult {
  entryLocale: any
  location: any
  menuGroups: any
  name: any
  node_locale: any
  legalFooterOnly: any
  socialMediaFooterOnly: any
}

const Footer = ({ minimalFooter, locale, footerData }: any) => {
  const languageSelector = useRef<any>()
  let bodyElement: any
  const [contentfulNavigation, setContentfulNavigation] = useState<NavigationResult | null>(null)
  const [languageSwitcherOpen, setLanguageSwitcherOpen] = useState(false)

  const navEntryLocale = locale ? locale : 'en-US'

  const handleAccordionOpen = (event: MouseEvent) => {
    const target = event.target as HTMLDivElement
    const idForElem = target.id.replace('-parent', '-list')
    const itemToAdd = document.getElementById(idForElem)

    if (itemToAdd) {
      if (itemToAdd.classList.contains('is-active')) {
        target.classList.remove('is-active')
        itemToAdd.classList.remove('is-active')
      } else {
        target.classList.add('is-active')
        itemToAdd.classList.add('is-active')
      }
    }
  }

  const handleLanguageSwitcherClick = (e: any) => {
    e.currentTarget.classList.toggle('is-active')
    setLanguageSwitcherOpen(!languageSwitcherOpen)
  }

  const createTextBasedOnType = (item: any) => {
    if (item.__typename === ModuleNames.PAGE_PRODUCT || item.__typename === ModuleNames.PAGE_FLEX) {
      return item.name
    } else if (item.__typename === ModuleNames.TOPIC_CTA) {
      return item.text
    }
  }

  const generateMenuColumn = (pages: any) => {
    return pages.map((page: any, n: number) => {
      let target
      const linkHref = createLinkBasedOnType(page)
      if (isExternalURL(linkHref)) {
        target = '_blank'
      } else {
        target = '_self'
      }
      return (
        <span key={page.id}>
          <a href={createLinkBasedOnType(page)} target={target} rel="noopener">
            {createTextBasedOnType(page)}
          </a>
        </span>
      )
    })
  }

  let dynamicData = footerData

  const generateEnglishNav = () => {
    return dynamicData.allContentfulNavMenu.nodes.filter((item: any) => {
      const selectedEntrylocale = item.entryLocale || LocaleCodes.ENGLISH.name
      return (
        item.node_locale === LocaleCodes.ENGLISH.code &&
        selectedEntrylocale === LocaleCodes.ENGLISH.name
      )
    })
  }

  const generateLocalizedNav = () => {
    return dynamicData.allContentfulNavMenu.nodes.filter((item: any) => {
      const localeInfo = mapLocale(item.entryLocale)
      if (localeInfo && localeInfo.pageLocale) {
        const pageLocale = localeInfo.pageLocale

        return (
          navEntryLocale === pageLocale &&
          navEntryLocale === item.node_locale &&
          pageLocale === item.node_locale
        )
      }
    })
  }

  const renderAppropriateNav = () => {
    let chosenNav = null

    if (Array.isArray(dynamicData?.allContentfulNavMenu?.nodes)) {
      const englishNav = generateEnglishNav()

      if (navEntryLocale && navEntryLocale === LocaleCodes.ENGLISH.code) {
        if (englishNav?.length && englishNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(englishNav[0])
          }

          chosenNav = englishNav[0]
        }
      } else {
        const localizedNav = generateLocalizedNav()
        if (localizedNav?.length && localizedNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(localizedNav[0])
          }

          chosenNav = localizedNav[0]
        } else if (englishNav?.length && englishNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(englishNav[0])
          }

          chosenNav = englishNav[0]
        }
      }
    }

    setContentfulNavigation(chosenNav)
    return chosenNav
  }

  const makeDynamicFooterCols = (menuData: any) => {
    let footerCols
    if (
      menuData &&
      menuData.menuGroups &&
      menuData.menuGroups.length &&
      menuData.menuGroups.length > 0
    ) {
      footerCols = menuData.menuGroups.map((menuGroup: any, i: number) => {
        let groupId
        if (menuGroup.newGroupName === 'About RPA') {
          groupId = 'about-rpa'
        } else {
          groupId = menuGroup.newGroupName?.toLowerCase()
        }

        const parentLinkHref = menuGroup.parentLink
          ? createLinkBasedOnType(menuGroup.parentLink)
          : '/'

        return (
          <div
            className="MainFooterRev-column"
            key={`menuGroup-parent--${menuGroup.newGroupName}-${i}`}>
            <div className="MainFooterRev-column-name">
              <div
                className="MainFooterRev-column-name"
                onClick={handleAccordionOpen}
                id={`group-${i}-parent`}>
                {menuGroup.parentLink && parentLinkHref ? (
                  <a href={parentLinkHref} className="MainFooterRev-parent-link">
                    {menuGroup.newGroupName}
                  </a>
                ) : (
                  <span>{menuGroup.newGroupName}</span>
                )}
                <WrapperImage
                  className="footer-down-arrow"
                  src={downArrowWhite}
                  alt="down arrow"
                  height={16}
                  width={16}
                  threshold={650}
                />
                <WrapperImage
                  className="footer-up-arrow"
                  src={upArrowWhite}
                  alt="up arrow"
                  height={16}
                  width={16}
                  threshold={650}
                />
              </div>
            </div>
            <div id={`group-${i}-list`} className="MainFooterRev-deepList">
              {generateMenuColumn(menuGroup.pages)}
            </div>
          </div>
        )
      })
    }
    return <div className="MainFooterRev-container">{footerCols}</div>
  }

  const generateLegalLinks = (legalFooter: any) => {
    return legalFooter.map((item: any, i: number) => {
      return (
        <span key={item.text}>
          <a
            href={item.internalLink ? internalLinkHandler(item.internalLink) : item.externalUrl}
            rel="noopener">
            {item.text}
          </a>
        </span>
      )
    })
  }

  const generateSocials = (menuData: any) => {
    let socialUrls
    if (
      menuData &&
      menuData.socialMediaFooterOnly &&
      menuData.socialMediaFooterOnly[0] &&
      menuData.socialMediaFooterOnly[0].values &&
      menuData.socialMediaFooterOnly[0].values.length &&
      menuData.socialMediaFooterOnly[0].values.length > 0
    ) {
      socialUrls = menuData.socialMediaFooterOnly[0].values
      return <SocialMediaIcons socialUrls={socialUrls} navEntryLocale={navEntryLocale} />
    }
  }

  const toggle = (e: any) => {
    if (languageSwitcherOpen) {
      setLanguageSwitcherOpen(false)
    }
  }

  const closeLanguageSelector = (e: any) => {
    if (!e.target.closest('.SecondaryFooter-container-mobile')) {
      document.querySelector('.SecondaryFooter-languageSwitcher')?.classList?.remove('is-active')
    }
  }

  useEffect(() => {
    if (typeof document !== undefined && typeof window !== undefined) {
      if (languageSwitcherOpen) window.addEventListener('mousedown', toggle, false)
      return () => {
        return window.removeEventListener('mousedown', toggle, false)
      }
    }
  }, [languageSwitcherOpen])

  useEffect(() => {
    renderAppropriateNav()
  }, [locale])

  useEffect(() => {
    window.addEventListener('resize', () => {
      const WeChatPopupResize = document.getElementById('popover_wechat')
      WeChatPopupResize?.removeAttribute('style')
      if (window.innerWidth > 767) {
        WeChatPopupResize?.classList.remove('open')
      }
    })

    window.addEventListener('click', closeLanguageSelector, false)
  }, [])

  const getLangTitle = (title: string) => {
    switch (title) {
      case 'Spanish':
        return 'Español'

      case 'Portuguese':
        return 'Português'

      default:
        return title
    }
  }

  const generateLanguageSelect = () => {
    return LanguageSelect.rows.map((row: any, i: number) => {
      const langTitle = getLangTitle(row.title)
      if (row.title === 'Taiwan') return null

      if (row.code !== navEntryLocale) {
        return (
          <span key={`language-select-footer--${i}`}>
            <a data-language={row.lang} href={row.href} rel="noopener">
              {langTitle}
            </a>
          </span>
        )
      }
    })
  }

  const dynamicFooter = () => {
    let menuData = contentfulNavigation
    let languageDisplayName = ''

    languageDisplayName = returnMatchingLocaleInfo(navEntryLocale)
    if (languageDisplayName === LocaleCodes.ENGLISH.name) {
      languageDisplayName = 'English'
    }

    return (
      <FooterWrapper $minimalFooter={minimalFooter}>
        <footer className="MainFooterRev MainFooterRev--rebranded">
          {!minimalFooter && makeDynamicFooterCols(menuData)}
          <section className="MainFooterRev-minifooter">
            <div className="MainFooterRev-minifooter--socialContainer">
              <div className="MainFooterRev-minifooter--logo">
                <WrapperImage
                  src={navEntryLocale !== LocaleCodes.CHINESE.code ? logoWhite : logoWhiteTm}
                  alt="UiPath Logo"
                  width={91}
                  height={32}
                  threshold={650}
                />
              </div>

              <div className="SecondaryFooter-container-mobile">
                <div
                  className="SecondaryFooter-languageSwitcher"
                  onClick={handleLanguageSwitcherClick}>
                  <div
                    className={`SecondaryFooter-languageList`}
                    id="SecondaryFooter-Language-List-Container">
                    {generateLanguageSelect()}
                  </div>

                  <div className="SecondaryFooter-languageList-select">
                    <span id="SecondaryFooter-languageList-text-desktop">
                      {languageDisplayName}
                    </span>
                    {minimalFooter ? (
                      <svg
                        width="6"
                        height="5"
                        viewBox="0 0 6 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="footer-down-caret">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0.239957 1.37345C0.176853 1.31086 0.141357 1.22566 0.141357 1.13678C0.141357 1.0479 0.176853 0.962705 0.239957 0.900116L0.37329 0.766782C0.436007 0.701677 0.5229 0.665472 0.61329 0.666782H5.38662C5.47701 0.665472 5.56391 0.701677 5.62662 0.766782L5.75996 0.900116C5.82306 0.962705 5.85856 1.0479 5.85856 1.13678C5.85856 1.22566 5.82306 1.31086 5.75996 1.37345L3.23329 3.90012C3.1728 3.96457 3.08835 4.00113 2.99996 4.00113C2.91157 4.00113 2.82711 3.96457 2.76662 3.90012L0.239957 1.37345Z"
                          fill="white"
                        />
                      </svg>
                    ) : (
                      <>
                        <WrapperImage
                          className="footer-down-arrow"
                          src={downArrowWhite}
                          alt="down arrow"
                          height={16}
                          width={16}
                          threshold={650}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="MainFooterRev-minifooter--socialIcons">
                <div className="SecondaryFooter-column">
                  <div className="MainFooterRev-social-networks">{generateSocials(menuData)}</div>
                  {navEntryLocale === LocaleCodes.CHINESE.code ? <WeChatPopUp /> : null}
                </div>
              </div>
            </div>

            <div className="MainFooterRev-minifooter--bottomText">
              <div className="MainFooterRev-minifooter-top">
                <div className="MainFooterRev-minifooter--legalLinks">
                  {/* We need to wrap the stateful component in a div to prevent it it from rerendering the parent element.
                The View Cookie Settings is needed to interact with the OT script loaded from GTM and would break if rerendered. */}
                  {menuData?.legalFooterOnly?.length > 0 && (
                    <div>{generateLegalLinks(menuData?.legalFooterOnly)}</div>
                  )}
                  <span key="View Cookie Settings">
                    <a id="ot-sdk-btn" className="ot-sdk-show-settings" rel="noopener">
                      View Cookie Settings
                    </a>
                  </span>
                </div>
                <div className="MainFooterRev-minifooter--copyright">
                  <CopyRightLinks footerData={footerData} navEntryLocale={navEntryLocale} />
                </div>
              </div>
              <div className="MainFooterRev-minifooter-bottom">
                {navEntryLocale !== LocaleCodes.CHINESE.code ? (
                  <DisclaimerText footerData={footerData} />
                ) : null}
              </div>
            </div>
          </section>
        </footer>
      </FooterWrapper>
    )
  }

  if (dynamicData) {
    return dynamicFooter()
  } else {
    return null
  }
}

export default Footer
